/**
 * Family rules in one place. Every feature that depends on the player's age
 * (names, purchases, ads) asks here, so a new feature cannot forget kid mode.
 *
 * Until the age screen is answered the player is treated as a kid: the safe
 * default.
 *
 * The paper fortune teller needs no age rule: its draws cost free tickets or
 * coins earned by playing (coins are never sold), never real money, and a day
 * has a cap on coin draws for everyone. Real-money random draws are ruled out
 * in docs/game-design.md; anything like them would have to be gated here.
 */
import type { AgeGroup, SaveData } from './model';

type AgeInfo = Pick<SaveData, 'ageGroup'>;

/** Kid mode: under 13, or not answered yet. */
export function isKid(s: AgeInfo): boolean {
  return s.ageGroup === null || s.ageGroup === 'under13';
}

/** Under 18 (or unknown): a grown-up must pass the parent gate before money is spent. */
export function needsParentGate(s: AgeInfo): boolean {
  return s.ageGroup !== 'adult';
}

/** Kids pick a server-generated name; they never type one others can see. */
export function canTypeOnlineName(s: AgeInfo): boolean {
  return !isKid(s);
}

/** The world chat (typed text from strangers) is for 13 and up; the server also refuses kid-mode accounts. */
export function canUseWorldChat(s: AgeInfo): boolean {
  return !isKid(s);
}

/** Show-off pictures on Facebook and other social apps (13+, like the apps themselves); the server also refuses kid-mode accounts. */
export function canShareOutside(s: AgeInfo): boolean {
  return !isKid(s);
}

/** Ads, if they are ever added, may only be personalised for adults. */
export function allowPersonalisedAds(s: AgeInfo): boolean {
  return s.ageGroup === 'adult';
}

/**
 * Age band for a birth year. Only the year is asked, so the birthday may not
 * have come yet: use the youngest possible age.
 */
export function ageGroupFor(birthYear: number, now = new Date()): AgeGroup {
  const age = now.getFullYear() - birthYear - 1;
  return age < 13 ? 'under13' : age < 18 ? 'teen' : 'adult';
}
